import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j202Api, FJ120, FJ121 } from '../../../services/j202Api';
import { Mode } from './useJ202Mode';
import axios from 'axios';

export function useJ202Record() {
  const [record, setRecord] = useState<FJ120 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ120>>({});
  const [details, setDetails] = useState<Partial<FJ121>[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await j202Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ120, value: FJ120[keyof FJ120]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ120): FJ120[keyof FJ120] => {
    return (formData as Record<string, FJ120[keyof FJ120]>)[key] ?? '';
  }, [formData]);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ120) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FJ120,
        details: details as FJ121[],
      };
      if (mode === 'add') {
        const result = await j202Api.create(req);
        message.success(`新增成功: ${result.fj120_01}`);
        onSuccess(result);
      } else {
        const result = await j202Api.update(formData.fj120_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          const msgs = errors.map((e: { message?: string }) => e.message || '驗證錯誤').join('\n');
          message.error(msgs);
        } else {
          message.error('驗證失敗');
        }
      } else {
        const errMsg = err instanceof Error ? err.message : '儲存失敗';
        message.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fj120_01) return;
    setLoading(true);
    try {
      await j202Api.delete(record.fj120_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fj120_80: 'N', fj120_13: false as unknown as boolean });
    setDetails([]);
  }, []);

  const applyResult = useCallback((result: FJ120) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fj121_02: prev.length + 1,
        fj121_10: '', fj121_11: '', fj121_12: '', fj121_17: '',
        fj121_13: 0, fj121_14: '', fj121_15: 0, fj121_16: 0,
      },
    ]);
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, fj121_02: i + 1 })));
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
